import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePlayModalContext } from '../contexts/PlayModalContext';
import { useGameSession } from '../contexts/GameSessionContext';

const overflowLinks = [
  { to: '/pass', name: 'Pass' },
  { to: '/rules', name: 'Rules' },
  { to: '/store', name: 'Store' },
  { to: '/community', name: 'Community' },
  { to: '/', name: 'Home' },
];

/** Slim chrome for /play and /queue — maximizes board space. */
export default function GameChromeNav() {
  const { pathname } = useLocation();
  const { openPlayModal } = usePlayModalContext();
  const { session } = useGameSession();
  const inQueue = pathname.startsWith('/queue');
  const inMatch = pathname.startsWith('/play/');

  return (
    <nav
      className="relative z-40 flex h-11 shrink-0 items-center justify-between border-b border-bk-gold-dim/40 bg-bk-bg-mid/95 px-2 backdrop-blur-md sm:h-12 sm:px-4"
      aria-label="Game"
    >
      <Link
        to="/"
        className="group shrink-0 font-extrabold tracking-tight text-bk-text transition-colors hover:text-bk-gold-bright"
      >
        Blind<span className="text-bk-gold">Kings</span>
      </Link>

      <div className="flex items-center gap-1 sm:gap-2">
        {inQueue ? (
          <span className="text-xs font-semibold uppercase tracking-wider text-bk-gold sm:text-sm">
            Matchmaking
          </span>
        ) : inMatch ? (
          session ? (
            <>
              {session.canReset ? (
                <button
                  type="button"
                  disabled={session.resetting}
                  onClick={() => session.resetGame?.()}
                  className="cursor-pointer px-2 py-1 text-xs font-bold uppercase tracking-wider text-bk-gold hover:text-bk-gold-bright disabled:opacity-50 sm:text-sm"
                >
                  {session.resetting ? 'Resetting…' : 'Reset'}
                </button>
              ) : null}
              {session.isGameOver ? (
                <Link
                  to="/"
                  className="px-2 py-1 text-xs font-bold uppercase tracking-wider text-bk-muted hover:text-bk-gold-bright sm:text-sm"
                >
                  Home
                </Link>
              ) : (
                <button
                  type="button"
                  disabled={session.leaving}
                  onClick={session.requestLeave}
                  className="cursor-pointer px-2 py-1 text-xs font-bold uppercase tracking-wider text-bk-danger-bright hover:text-bk-danger disabled:opacity-50 sm:text-sm"
                >
                  {session.leaving
                    ? 'Leaving…'
                    : session.canReset
                      ? 'Quit'
                      : 'Forfeit'}
                </button>
              )}
            </>
          ) : null
        ) : (
          <button
            type="button"
            onClick={openPlayModal}
            className="cursor-pointer px-2 py-1 text-xs font-bold uppercase tracking-wider text-bk-gold hover:text-bk-gold-bright sm:text-sm"
          >
            Modes
          </button>
        )}
        <Menu as="div" className="relative">
          <MenuButton className="inline-flex items-center justify-center rounded-md p-1.5 text-bk-muted transition-colors hover:text-bk-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-bk-gold">
            <span className="sr-only">Open game menu</span>
            <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
          </MenuButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-100"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="absolute right-0 z-50 mt-2 w-44 origin-top-right border border-bk-gold-dim/50 bg-bk-panel py-1 focus:outline-none">
              {overflowLinks.map(({ to, name }) => (
                <MenuItem key={name}>
                  {({ focus }) => (
                    <Link
                      to={to}
                      className={`block px-4 py-2 text-sm text-bk-text ${
                        focus ? 'bg-bk-panel-raised text-bk-gold' : ''
                      }`}
                    >
                      {name}
                    </Link>
                  )}
                </MenuItem>
              ))}
            </MenuItems>
          </Transition>
        </Menu>
      </div>
    </nav>
  );
}
